import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  trackSocialClick,
  trackEmailClick,
  trackCallClick,
} from "../utils/analytics";
import LanguageSwitcher from "./LanguageSwitcher";
import "../styles/Layout.css";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Get translated hours based on current language
  const getHours = (locationKey: "montreal" | "delson") => {
    return t(`locations.${locationKey}.hours`);
  };

  return (
    <div className="layout">
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-to-content">
        {t("common.skipToContent")}
      </a>

      {/* Header */}
      <header className="header">
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="logo" onClick={closeMenu}>
            <div className="logo-wrapper">
              <img
                src="https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-480x270.png"
                srcSet="https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-480x270.png 1x, https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-960x540.png 2x"
                alt="La Cava Grecque"
                className="logo-image"
                width="480"
                height="270"
                loading="eager"
              />
              <span className="logo-tagline">
                {i18n.language === "fr" ? "Depuis 1989" : "Since 1989"}
              </span>
            </div>
          </Link>

          {/* Mobile Language Switcher - next to logo */}
          <div className="mobile-language-wrapper">
            <LanguageSwitcher variant="mobile" />
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <Link
              to="/montreal"
              className={
                location.pathname.includes("/montreal") ? "active" : ""
              }
            >
              {t("nav.montreal").toUpperCase()}
            </Link>
            <Link
              to="/delson"
              className={location.pathname.includes("/delson") ? "active" : ""}
            >
              {t("nav.delson").toUpperCase()}
            </Link>
            <Link
              to="/order"
              className={location.pathname === "/order" ? "active" : ""}
            >
              {t("nav.order").toUpperCase()}
            </Link>
            <Link
              to="/reservations"
              className={location.pathname === "/reservations" ? "active" : ""}
            >
              {t("common.reservations").toUpperCase()}
            </Link>
            <LanguageSwitcher variant="header" />
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className={`mobile-menu-toggle ${menuOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? t("common.closeMenu") : t("common.openMenu")}
          >
            <div className="hamburger">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <>
            <div className="menu-overlay" onClick={closeMenu}></div>
            <nav className="mobile-nav">
              <Link
                to="/montreal"
                onClick={closeMenu}
                className={
                  location.pathname.includes("/montreal") ? "active" : ""
                }
              >
                {t("nav.montreal").toUpperCase()}
              </Link>
              <Link
                to="/delson"
                onClick={closeMenu}
                className={
                  location.pathname.includes("/delson") ? "active" : ""
                }
              >
                {t("nav.delson").toUpperCase()}
              </Link>
              <Link
                to="/order"
                onClick={closeMenu}
                className={location.pathname === "/order" ? "active" : ""}
              >
                {t("nav.order").toUpperCase()}
              </Link>
              <Link
                to="/reservations"
                onClick={closeMenu}
                className={
                  location.pathname === "/reservations" ? "active" : ""
                }
              >
                {t("common.reservations").toUpperCase()}
              </Link>
            </nav>
          </>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" className="main-content">
        {children}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          {/* Footer Logo */}
          <div className="footer-logo">
            <Link to="/" onClick={closeMenu}>
              <img
                src="https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-480x270.png"
                srcSet="https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-480x270.png 1x, https://cdn.jsdelivr.net/gh/mariusroyale/static-assets@main/cava/images/logo-960x540.png 2x"
                alt="La Cava Grecque"
                className="footer-logo-image"
                width="480"
                height="270"
                loading="lazy"
              />
              <span className="footer-logo-tagline">
                {i18n.language === "fr" ? "Depuis 1989" : "Since 1989"}
              </span>
            </Link>
          </div>

          <div className="footer-locations">
            {/* Montreal Location */}
            <div className="footer-location">
              <h3 className="footer-location-title">
                {i18n.language === "en" ? "MONTREAL" : "MONTRÉAL"}
              </h3>
              <div className="footer-location-hours">
                {getHours("montreal")}
              </div>
              <div className="footer-location-address">
                {i18n.language === "fr" ? (
                  <>
                    2532 rue Ste-Catherine E
                    <br />
                    Montréal, QC H2K 2K2
                  </>
                ) : (
                  <>
                    2532 St Catherine St E
                    <br />
                    Montreal, QC H2K 2K2
                  </>
                )}
              </div>
              <a
                href="tel:5145272532"
                className="footer-location-phone"
                onClick={() => trackCallClick("montreal", "514-527-2532")}
              >
                514-527-2532
              </a>
            </div>

            {/* Delson Location */}
            <div className="footer-location">
              <h3 className="footer-location-title">DELSON</h3>
              <div className="footer-location-hours">{getHours("delson")}</div>
              <div className="footer-location-address">
                35 Boulevard Georges-Gagné Sud
                <br />
                Delson, QC J5B 2E4
              </div>
              <a
                href="tel:4506325320"
                className="footer-location-phone"
                onClick={() => trackCallClick("delson", "450-632-5320")}
              >
                450-632-5320
              </a>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="footer-social">
            <a
              href="https://www.facebook.com/profile.php?id=61572257114841"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="footer-social-link"
              onClick={() => trackSocialClick("facebook")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/la_cava_grecque/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="footer-social-link"
              onClick={() => trackSocialClick("instagram")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@la_cava_grecque?_r=1&_t=ZS-91jmjDBhehE"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="footer-social-link"
              onClick={() => trackSocialClick("tiktok")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@La_Cava_Grecque"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="footer-social-link"
              onClick={() => trackSocialClick("youtube")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </a>
            <a
              href="mailto:lacavagrecque@hotmail.com"
              aria-label="Email"
              className="footer-social-link"
              onClick={() => trackEmailClick("footer")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </a>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
            <LanguageSwitcher variant="footer" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
